import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Snackbar,
  Chip,
  Stack,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Stadium as FieldIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsAdmin } from '../../../contexts/PermissionsContext';

const VenueManagementPage = () => {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Venue dialog state
  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [venueDialogMode, setVenueDialogMode] = useState('create');
  const [editingVenue, setEditingVenue] = useState(null);
  const [venueName, setVenueName] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [saving, setSaving] = useState(false);
  const [venueError, setVenueError] = useState(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingVenue, setDeletingVenue] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Field dialog state
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [fieldVenueId, setFieldVenueId] = useState(null);
  const [fieldName, setFieldName] = useState('');
  const [savingField, setSavingField] = useState(false);
  const [fieldError, setFieldError] = useState(null);

  // Delete field state
  const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);
  const [deletingField, setDeletingField] = useState(null);
  const [deletingFieldVenueName, setDeletingFieldVenueName] = useState('');
  const [deletingFieldLoading, setDeletingFieldLoading] = useState(false);

  // Expanded rows (for showing fields)
  const [expandedRows, setExpandedRows] = useState({});

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApi.getVenues();
      setVenues(data || []);
    } catch {
      setError('Failed to load venues.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  // Toggle expanded row
  const toggleRow = (venueId) => {
    setExpandedRows((prev) => ({ ...prev, [venueId]: !prev[venueId] }));
  };

  // Venue CRUD handlers
  const handleOpenCreate = () => {
    setVenueDialogMode('create');
    setEditingVenue(null);
    setVenueName('');
    setVenueAddress('');
    setVenueError(null);
    setVenueDialogOpen(true);
  };

  const handleOpenEdit = (venue) => {
    setVenueDialogMode('edit');
    setEditingVenue(venue);
    setVenueName(venue.name);
    setVenueAddress(venue.address || '');
    setVenueError(null);
    setVenueDialogOpen(true);
  };

  const handleVenueDialogClose = () => {
    setVenueDialogOpen(false);
    setVenueError(null);
  };

  const handleVenueSave = async () => {
    if (!venueName.trim()) {
      setVenueError('Name is required.');
      return;
    }
    setSaving(true);
    setVenueError(null);
    try {
      if (venueDialogMode === 'create') {
        await unifiedApi.createVenue({ name: venueName.trim(), address: venueAddress.trim() || null });
        setSnackbar({ open: true, message: 'Venue created successfully.' });
      } else {
        await unifiedApi.updateVenue(editingVenue.id, {
          id: editingVenue.id,
          name: venueName.trim(),
          address: venueAddress.trim() || null,
        });
        setSnackbar({ open: true, message: 'Venue updated successfully.' });
      }
      setVenueDialogOpen(false);
      fetchVenues();
    } catch {
      setVenueError('Failed to save venue.');
    } finally {
      setSaving(false);
    }
  };

  // Delete venue handlers
  const handleOpenDelete = (venue) => {
    setDeletingVenue(venue);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingVenue) return;
    setDeleting(true);
    try {
      await unifiedApi.deleteVenue(deletingVenue.id);
      setSnackbar({ open: true, message: `"${deletingVenue.name}" deleted.` });
      setDeleteDialogOpen(false);
      setDeletingVenue(null);
      fetchVenues();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete venue. It may be in use by events.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeletingVenue(null);
  };

  // Field handlers
  const handleOpenAddField = (venueId) => {
    setFieldVenueId(venueId);
    setFieldName('');
    setFieldError(null);
    setFieldDialogOpen(true);
  };

  const handleFieldDialogClose = () => {
    setFieldDialogOpen(false);
    setFieldError(null);
  };

  const handleFieldSave = async () => {
    if (!fieldName.trim()) {
      setFieldError('Field name is required.');
      return;
    }
    setSavingField(true);
    setFieldError(null);
    try {
      await unifiedApi.createField({ name: fieldName.trim(), venueId: fieldVenueId });
      setSnackbar({ open: true, message: 'Field added successfully.' });
      setFieldDialogOpen(false);
      fetchVenues();
    } catch {
      setFieldError('Failed to add field.');
    } finally {
      setSavingField(false);
    }
  };

  const handleOpenDeleteField = (field, venueName) => {
    setDeletingField(field);
    setDeletingFieldVenueName(venueName);
    setDeleteFieldDialogOpen(true);
  };

  const handleDeleteFieldConfirm = async () => {
    if (!deletingField) return;
    setDeletingFieldLoading(true);
    try {
      await unifiedApi.deleteField(deletingField.id);
      setSnackbar({ open: true, message: `Field "${deletingField.name}" deleted.` });
      setDeleteFieldDialogOpen(false);
      setDeletingField(null);
      fetchVenues();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete field. It may be in use by games.' });
    } finally {
      setDeletingFieldLoading(false);
    }
  };

  const handleDeleteFieldCancel = () => {
    setDeleteFieldDialogOpen(false);
    setDeletingField(null);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5">Venue Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isAdmin && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add Venue
          </Button>
        </Box>
      )}

      {/* Desktop Table */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell align="center">Fields</TableCell>
                {isAdmin && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {venues.length === 0 && !error ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4} align="center">
                    No venues found. Add your first venue to get started.
                  </TableCell>
                </TableRow>
              ) : (
                venues.map((venue) => (
                  <React.Fragment key={venue.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(venue.id)}
                          aria-label={expandedRows[venue.id] ? 'Collapse fields' : 'Expand fields'}
                        >
                          {expandedRows[venue.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {venue.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{venue.address || '—'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={venue.fields?.length || 0}
                          size="small"
                          variant="outlined"
                          clickable={false}
                          onClick={() => {}}
                        />
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenEdit(venue)} aria-label={`Edit ${venue.name}`}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleOpenDelete(venue)} aria-label={`Delete ${venue.name}`}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                    {/* Expanded fields row */}
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 5 : 4} sx={{ py: 0, borderBottom: expandedRows[venue.id] ? undefined : 'none' }}>
                        <Collapse in={expandedRows[venue.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, pl: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                              <FieldIcon fontSize="small" color="action" />
                              <Typography variant="subtitle2" fontWeight="bold">
                                Fields
                              </Typography>
                              {isAdmin && (
                                <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenAddField(venue.id)}>
                                  Add Field
                                </Button>
                              )}
                            </Stack>
                            {venue.fields && venue.fields.length > 0 ? (
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {venue.fields.map((field) => (
                                  <Chip
                                    key={field.id}
                                    label={field.name}
                                    size="small"
                                    variant="outlined"
                                    onDelete={isAdmin ? () => handleOpenDeleteField(field, venue.name) : undefined}
                                    clickable={!isAdmin ? false : undefined}
                                    onClick={!isAdmin ? () => {} : undefined}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No fields configured for this venue.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {venues.length === 0 && !error ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No venues found. Add your first venue to get started.
          </Typography>
        ) : (
          venues.map((venue) => (
            <Paper key={venue.id} elevation={1} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="medium">
                    {venue.name}
                  </Typography>
                  {venue.address && (
                    <Typography variant="body2" color="text.secondary">
                      {venue.address}
                    </Typography>
                  )}
                </Box>
                {isAdmin && (
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => handleOpenEdit(venue)} aria-label={`Edit ${venue.name}`}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleOpenDelete(venue)} aria-label={`Delete ${venue.name}`}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <FieldIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Fields ({venue.fields?.length || 0})
                  </Typography>
                  {isAdmin && (
                    <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenAddField(venue.id)}>
                      Add
                    </Button>
                  )}
                </Stack>
                {venue.fields && venue.fields.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {venue.fields.map((field) => (
                      <Chip
                        key={field.id}
                        label={field.name}
                        size="small"
                        variant="outlined"
                        onDelete={isAdmin ? () => handleOpenDeleteField(field, venue.name) : undefined}
                        clickable={!isAdmin ? false : undefined}
                        onClick={!isAdmin ? () => {} : undefined}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No fields configured.
                  </Typography>
                )}
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {/* Create/Edit Venue Dialog */}
      <Dialog open={venueDialogOpen} onClose={handleVenueDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{venueDialogMode === 'create' ? 'Add Venue' : 'Edit Venue'}</DialogTitle>
        <DialogContent>
          {venueError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {venueError}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Name"
            fullWidth
            required
            value={venueName}
            onChange={(e) => setVenueName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Address"
            fullWidth
            value={venueAddress}
            onChange={(e) => setVenueAddress(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVenueDialogClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleVenueSave} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : venueDialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Venue Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Venue</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingVenue?.name}</strong>?
            {deletingVenue?.fields?.length > 0 && (
              <> This will also delete {deletingVenue.fields.length} field{deletingVenue.fields.length !== 1 ? 's' : ''}.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Field Dialog */}
      <Dialog open={fieldDialogOpen} onClose={handleFieldDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Field</DialogTitle>
        <DialogContent>
          {fieldError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {fieldError}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Field Name"
            fullWidth
            required
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            sx={{ mt: 1 }}
            inputProps={{ maxLength: 200 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFieldDialogClose} disabled={savingField}>Cancel</Button>
          <Button onClick={handleFieldSave} variant="contained" disabled={savingField}>
            {savingField ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Field Confirmation Dialog */}
      <Dialog open={deleteFieldDialogOpen} onClose={handleDeleteFieldCancel}>
        <DialogTitle>Delete Field</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete field <strong>{deletingField?.name}</strong> from <strong>{deletingFieldVenueName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteFieldCancel} disabled={deletingFieldLoading}>Cancel</Button>
          <Button onClick={handleDeleteFieldConfirm} color="error" variant="contained" disabled={deletingFieldLoading}>
            {deletingFieldLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </AdminLayout>
  );
};

export default VenueManagementPage;
